/**
 * AnimF32: Phira's keyframe tracks over the IR, as typed arrays (W1-05a engine-eval; rpe-format.md F§6.2-6.7,
 * F§9.2, F§9.7, R§2.1, DESIGN.md §6.2, ARCHITECTURE.md §7.1 numeric policy).
 *
 * `parse_events` semantics, literally:
 *  - every event becomes TWO keyframes in list order: (sec(startTime), start, the event's tween) and
 *    (sec(endTime), end, hold);
 *  - every keyframe value is pre-multiplied by the property factor IN f32 before any interpolation:
 *    `fround(fround(v) * fround(factor))` (alpha 1/255, moveX 2/1350, moveY 2/900, rotate -1, speed SPEED_RATIO,
 *    scale 1 / 0.5 / 2/1350);
 *  - a default keyframe (0 s, default, hold) is prepended for colour (white), text (""), incline (0) and paint (-1)
 *    when the first event does not start at beat 0 (`a + b/c != 0`); alpha, move, rotate and scale extrapolate;
 *  - the event tween is Phira's `RPEEvent::tween()` (RPE_TWEEN_MAP, slice applicability, bezier through the global
 *    registry, see bezierRegistry.ts), dispatched by numeric id (no closure per keyframe).
 *
 * Evaluation is STATELESS. For sorted keyframes (all IR that satisfies its invariants) the cursor is the last
 * keyframe with time <= t, or 0 before the first keyframe (EXTRAPOLATION: progress < 0 is not clamped): Phira's
 * forward play from any earlier time, and the stateless reference (`refAnimAt`). The value is
 *   kf_i + (kf_{i+1} - kf_i) * tween_i(fround((t - t_i) / (t_{i+1} - t_i)))       in f32 (`F32.lerp` order)
 * and the last keyframe's value is held after it. A NaN time walks to the LAST keyframe: Phira's `set_time` walks
 * forward until the next keyframe is later than t (never, for NaN) and back while its own keyframe is later (never
 * either), so it yields the last value, from any history (R§2.4: a speed-0 note's NaN control key evaluates to the
 * control's last value). Each track keeps a cursor HINT so sequential playback is O(1) amortised; the hint never
 * changes results.
 *
 * Keyframe arrays that are not sorted (only reachable through hand-built IR) have history-dependent results in
 * Phira; they follow the literal forward play from a FRESH track (time 0, cursor 0), as the stateful reference
 * `refPlayForward` does: t = 0 (or -0) keeps cursor 0 (`set_time`'s "time unchanged" early return), any other t
 * walks forward from 0 while the next keyframe is not later than t. The sorted lookup does not model that early
 * return: the two differ only when a keyframe after the first also lies at time <= 0 (a zero-length event at 0 or
 * events at negative times), where Phira's result at exactly t = 0 depends on whether the track was played before.
 *
 * Also here: colour tracks (three f32 channels, white default), the GIF progress track (natural-speed auto-play
 * before, between and after events, up to 2000 s, `gifEvents: []` = auto-play from 0; F§9.7, D§6.2), `gifMsAt`
 * (Phira's `(progress * total_ms) as u128 % total_ms`) and sampling helpers.
 *
 * Internal API (W1-05b engine-graph, the other evaluators of this package):
 *   EvalEnv                              seconds of a triple + the bezier registry
 *   AnimF32.fromEvents(events, env, o)   numeric track (factor, optional default)
 *   AnimF32.fromKeyframes(...)           numeric track from explicit keyframes (floors, tests)
 *   anim.valueAt(t) / anim.cursorAt(t)   evaluation / active keyframe index
 *   anim.timeline.src[i]                 event index * 2 (+1 = the event's end keyframe), -1 = prepended default
 *   ColorAnim, GifTrack, gifMsAt, sampleInto
 */

import { createBezierCurve, type BezierCurve } from '../ease/bezier';
import { resolveRpe } from '../ease/rpeMap';
import { HOLD_TWEEN, LINEAR_TWEEN, ONE_TWEEN, staticY } from '../ease/tweens';
import type { BezierPoints, PhiraTweenDesc, TweenId } from '../ease/types';
import { Search } from '../math/search';
import type { Sec, Triple } from '../time/types';
import type { RGB } from '../util/types';
import type { BezierRegistry } from './bezierRegistry';

const fr = Math.fround;

// ---------------------------------------------------------------------------------------------------------------
// Environment and event shape
// ---------------------------------------------------------------------------------------------------------------

/** What compiling a track needs besides its events. */
export interface EvalEnv {
  /** Seconds of a (possibly non-normalised) beat triple in Phira's f64 order (`TempoMap.secAtTriple`). */
  readonly sec: (t: Triple) => Sec;
  /** Phira's global bezier map; null = every bezier event uses its own points. */
  readonly bezier: BezierRegistry | null;
}

/** Structural event (IrEvent satisfies it). */
export interface EventLike<V> {
  readonly startTime: Triple;
  readonly endTime: Triple;
  readonly start: V;
  readonly end: V;
  readonly easingType: number;
  readonly easingLeft: number;
  readonly easingRight: number;
  readonly bezier: number;
  readonly bezierPoints: BezierPoints;
}

/** `Triple::beats()`: a + b / c in f64, in this order. */
export function tripleBeats(t: Triple): number {
  return t[0] + t[1] / t[2];
}

// ---------------------------------------------------------------------------------------------------------------
// Tweens by numeric id
// ---------------------------------------------------------------------------------------------------------------

const K_STATIC = 0;
const K_CLAMPED = 1;
const K_BEZIER = 2;

/**
 * The tween of every keyframe as flat arrays: static id, clamped (id, slice) with the slice constants
 * precomputed exactly as `Ease.clampedY` computes them, or a prepared bezier curve.
 */
export class TweenTable {
  readonly kind: Uint8Array;
  readonly id: Uint8Array;
  readonly lo: Float32Array;
  readonly span: Float32Array;
  readonly fl: Float32Array;
  readonly den: Float32Array;
  readonly curves: (BezierCurve | null)[];

  constructor(n: number) {
    this.kind = new Uint8Array(n);
    this.id = new Uint8Array(n);
    this.lo = new Float32Array(n);
    this.span = new Float32Array(n);
    this.fl = new Float32Array(n);
    this.den = new Float32Array(n);
    this.curves = new Array<BezierCurve | null>(n).fill(null);
  }

  setStatic(i: number, id: TweenId): void {
    this.kind[i] = K_STATIC;
    this.id[i] = id;
  }

  setClamped(i: number, id: TweenId, l: number, r: number): void {
    const lo = fr(l);
    const hi = fr(r);
    this.kind[i] = K_CLAMPED;
    this.id[i] = id;
    this.lo[i] = lo;
    this.span[i] = fr(hi - lo);
    const fl = staticY(id, lo);
    this.fl[i] = fl;
    this.den[i] = fr(staticY(id, hi) - fl);
  }

  setCurve(i: number, curve: BezierCurve): void {
    this.kind[i] = K_BEZIER;
    this.curves[i] = curve;
  }

  /** Sets keyframe i from a resolved Phira tween descriptor (bezier through the registry). */
  setDesc(i: number, desc: PhiraTweenDesc, registry: BezierRegistry | null): void {
    switch (desc.kind) {
      case 'static':
        this.setStatic(i, desc.tween);
        return;
      case 'clamped':
        this.setClamped(i, desc.tween, desc.l, desc.r);
        return;
      case 'bezier':
        this.setCurve(i, registry === null ? createBezierCurve(desc.p) : registry.curve(desc.p));
        return;
    }
  }

  /** Tween output of keyframe i at f32 progress x (x unclamped). */
  y(i: number, x: number): number {
    switch (this.kind[i]) {
      case K_STATIC:
        return staticY(this.id[i] ?? HOLD_TWEEN, x);
      case K_CLAMPED: {
        const id = this.id[i] ?? HOLD_TWEEN;
        const lo = this.lo[i] ?? 0;
        const arg = fr(lo + fr((this.span[i] ?? 0) * fr(x)));
        return fr(fr(staticY(id, arg) - (this.fl[i] ?? 0)) / (this.den[i] ?? 0));
      }
      default: {
        const c = this.curves[i];
        return c === null || c === undefined ? Number.NaN : c.y(x);
      }
    }
  }

  /** True when keyframe i uses a bezier curve. */
  isBezier(i: number): boolean {
    return this.kind[i] === K_BEZIER;
  }

  /** True when keyframe i's tween ignores its progress (hold -> 0, one -> 1): any x gives the same y. */
  isProgressFree(i: number): boolean {
    return this.kind[i] === K_STATIC && (this.id[i] === HOLD_TWEEN || this.id[i] === ONE_TWEEN);
  }
}

/** Phira's `RPEEvent::tween()` of an event. */
export function eventTweenDesc(e: EventLike<unknown>): PhiraTweenDesc {
  return resolveRpe(e.easingType, e.easingLeft, e.easingRight, e.bezier, e.bezierPoints);
}

// ---------------------------------------------------------------------------------------------------------------
// Timeline: keyframe times, tweens, sources and the cursor
// ---------------------------------------------------------------------------------------------------------------

function isSortedTimes(times: Float64Array): boolean {
  for (let i = 1; i < times.length; i++) {
    const prev = times[i - 1] ?? Number.NaN;
    const cur = times[i] ?? Number.NaN;
    if (!(cur >= prev)) return false;
  }
  for (const t of times) if (Number.isNaN(t)) return false;
  return true;
}

/**
 * Phira's cursor after `set_time(t)` on a FRESH track (time 0, cursor 0), literally: t = 0 (or -0) hits the "time
 * unchanged" early return and keeps cursor 0; otherwise walk forward while the next keyframe is not later than t
 * (NaN keyframe times are walked past) and never back (the backward walk cannot leave 0). Unsorted keyframes only;
 * exported for the floor tracks.
 */
export function freshWalk(times: Float64Array, t: number): number {
  if (t === 0) return 0;
  const n = times.length;
  let i = 0;
  while (i + 1 < n && !((times[i + 1] ?? Number.NaN) > t)) i++;
  return i;
}

/** Keyframe times + tweens + provenance, with Phira's forward-play cursor. */
export class Timeline {
  readonly n: number;
  readonly times: Float64Array;
  readonly tween: TweenTable;
  /** Source of each keyframe: event index * 2 (+1 for the event's end keyframe); -1 for a prepended default. */
  readonly src: Int32Array;
  /** Keyframe times are non-decreasing (always true for IR that satisfies its invariants). */
  readonly sorted: boolean;
  private hint = 0;

  constructor(times: Float64Array, tween: TweenTable, src: Int32Array) {
    this.n = times.length;
    this.times = times;
    this.tween = tween;
    this.src = src;
    this.sorted = isSortedTimes(times);
  }

  private valid(i: number, t: number): boolean {
    const times = this.times;
    return (i === 0 || (times[i] ?? Number.NaN) <= t) && (i + 1 >= this.n || (times[i + 1] ?? Number.NaN) > t);
  }

  /**
   * Index of the keyframe whose segment is active at t: the last keyframe with time <= t for sorted keyframes (0
   * before the first), the last keyframe for a NaN t; unsorted keyframes replay Phira's forward play from a fresh
   * track (see the module comment).
   */
  cursorAt(t: number): number {
    const n = this.n;
    if (n <= 1) return 0;
    if (Number.isNaN(t)) return n - 1;
    if (!this.sorted) return freshWalk(this.times, t);
    const h = this.hint;
    if (h < n && this.valid(h, t)) return h;
    if (h + 1 < n && this.valid(h + 1, t)) {
      this.hint = h + 1;
      return h + 1;
    }
    let i = Search.upperBound(this.times, t) - 1;
    if (i < 0) i = 0;
    this.hint = i;
    return i;
  }

  /** f32 tween output for keyframe i at time t (i must have a successor). */
  yAt(i: number, t: number): number {
    const times = this.times;
    const t1 = times[i] as number;
    const t2 = times[i + 1] as number;
    return this.tween.y(i, fr((t - t1) / (t2 - t1)));
  }

  /** True when any keyframe uses a bezier curve. */
  usesBezier(): boolean {
    for (let i = 0; i < this.n; i++) if (this.tween.isBezier(i)) return true;
    return false;
  }

  /** Event index of keyframe i (-1 for a prepended default). */
  eventOf(i: number): number {
    const s = this.src[i] ?? -1;
    return s < 0 ? -1 : s >> 1;
  }

  /** True when keyframe i is an event's START keyframe (its segment is the event itself). */
  isEventStart(i: number): boolean {
    const s = this.src[i] ?? -1;
    return s >= 0 && (s & 1) === 0;
  }
}

/**
 * Builds the timeline of an event list (`parse_events`): two keyframes per event in list order, the start
 * keyframe with the event's tween, the end keyframe with hold; plus a prepended default keyframe when asked.
 * `tweenOf` overrides the event tween (the legacy speed integrator uses linear for every event).
 */
export function eventTimeline(
  events: ReadonlyArray<EventLike<unknown>>,
  env: EvalEnv,
  prependDefault: boolean,
  tweenOf?: (e: EventLike<unknown>) => PhiraTweenDesc,
): Timeline {
  const off = prependDefault ? 1 : 0;
  const n = events.length * 2 + off;
  const times = new Float64Array(n);
  const tween = new TweenTable(n);
  const src = new Int32Array(n);
  if (prependDefault) {
    times[0] = 0;
    tween.setStatic(0, HOLD_TWEEN);
    src[0] = -1;
  }
  events.forEach((e, k) => {
    const i = off + k * 2;
    times[i] = env.sec(e.startTime);
    times[i + 1] = env.sec(e.endTime);
    tween.setDesc(i, tweenOf === undefined ? eventTweenDesc(e) : tweenOf(e), env.bezier);
    tween.setStatic(i + 1, HOLD_TWEEN);
    src[i] = k * 2;
    src[i + 1] = k * 2 + 1;
  });
  return new Timeline(times, tween, src);
}

/** Whether `parse_events` prepends the default keyframe: a default exists and the first event is not at beat 0. */
export function needsDefault(events: ReadonlyArray<EventLike<unknown>>): boolean {
  const first = events[0];
  return first !== undefined && tripleBeats(first.startTime) !== 0;
}

// ---------------------------------------------------------------------------------------------------------------
// Numeric tracks
// ---------------------------------------------------------------------------------------------------------------

export interface NumberTrackOptions {
  /** Property factor, applied per keyframe in f32 (default 1). */
  readonly factor?: number;
  /** Default value prepended when the first event does not start at beat 0 (colour/text/incline/paint only). */
  readonly default?: number;
  /** Tween override (legacy speed: linear for every event). */
  readonly tweenOf?: (e: EventLike<unknown>) => PhiraTweenDesc;
}

/** `fround(fround(v) * fround(factor))`. */
export function factorValue(v: number, factor: number): number {
  return fr(fr(v) * fr(factor));
}

/**
 * The shared f32 of every keyframe value when every tween is HOLD or ONE (progress-independent), else null
 * (see AnimF32.constant).
 */
function constantOf(timeline: Timeline, values: Float32Array): number | null {
  const n = values.length;
  if (n === 0) return null;
  const v = values[0] as number;
  if (Number.isNaN(v) || Object.is(v, -0)) return null;
  for (let i = 0; i < n; i++) {
    if (!Object.is(values[i], v)) return null;
    if (!timeline.tween.isProgressFree(i)) return null;
  }
  return v;
}

export class AnimF32 {
  readonly timeline: Timeline;
  /** f32 keyframe values (factor already applied). */
  readonly values: Float32Array;
  /**
   * The track's constant value when every keyframe holds the same f32 and every tween ignores its progress
   * (HOLD -> 0, ONE -> 1), so `fr(v + fr((v - v) * y))` is exactly `v` for any time, including the NaN / infinite
   * progress of a zero-length segment. Null otherwise (absent / empty tracks evaluate NaN and are not constant;
   * `-0` is excluded so the shortcut cannot turn Phira's `fr(-0 + 0) = +0` into `-0`).
   */
  readonly constant: number | null;

  constructor(timeline: Timeline, values: Float32Array) {
    this.timeline = timeline;
    this.values = values;
    this.constant = constantOf(timeline, values);
  }

  /** Track of an event list with Phira's parse_events rules. */
  static fromEvents(events: ReadonlyArray<EventLike<number>>, env: EvalEnv, opts: NumberTrackOptions = {}): AnimF32 {
    const factor = fr(opts.factor ?? 1);
    const withDefault = opts.default !== undefined && needsDefault(events);
    const tl = eventTimeline(events, env, withDefault, opts.tweenOf);
    const values = new Float32Array(tl.n);
    const off = withDefault ? 1 : 0;
    if (withDefault) values[0] = fr(opts.default ?? 0);
    events.forEach((e, k) => {
      values[off + k * 2] = factorValue(e.start, factor);
      values[off + k * 2 + 1] = factorValue(e.end, factor);
    });
    return new AnimF32(tl, values);
  }

  /** Track from explicit keyframes (times f64, values rounded to f32, static tween ids). */
  static fromKeyframes(times: ArrayLike<number>, values: ArrayLike<number>, tweens: ArrayLike<TweenId>): AnimF32 {
    const n = times.length;
    const tw = new TweenTable(n);
    const src = new Int32Array(n).fill(-1);
    const ts = new Float64Array(n);
    const vs = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      ts[i] = times[i] ?? Number.NaN;
      vs[i] = values[i] ?? Number.NaN;
      tw.setStatic(i, tweens[i] ?? HOLD_TWEEN);
    }
    return new AnimF32(new Timeline(ts, tw, src), vs);
  }

  get length(): number {
    return this.timeline.n;
  }

  cursorAt(t: number): number {
    return this.timeline.cursorAt(t);
  }

  /** Phira's value at t (f32). NaN for an empty track. */
  valueAt(t: number): number {
    const k = this.constant;
    if (k !== null) return k;
    const tl = this.timeline;
    const n = tl.n;
    if (n === 0) return Number.NaN;
    const i = tl.cursorAt(t);
    const values = this.values;
    const v1 = values[i] as number;
    if (i + 1 >= n) return v1;
    const v2 = values[i + 1] as number;
    const y = tl.yAt(i, t);
    return fr(v1 + fr(fr(v2 - v1) * y));
  }

  /** Value of a fresh track that was never advanced: Phira's `now()` at time 0 with cursor 0 (frozen incline). */
  valueAtLoad(): number {
    const tl = this.timeline;
    const n = tl.n;
    if (n === 0) return Number.NaN;
    const values = this.values;
    const v1 = values[0] as number;
    if (n === 1) return v1;
    const v2 = values[1] as number;
    const y = tl.yAt(0, 0);
    return fr(v1 + fr(fr(v2 - v1) * y));
  }

  usesBezier(): boolean {
    return this.timeline.usesBezier();
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Colour tracks
// ---------------------------------------------------------------------------------------------------------------

/** `[r, g, b]` u8 -> channel / 255 in f32 (Phira's colour conversion). */
export function colorChannel(c: number): number {
  return fr(c / 255);
}

/** Colour track: per-channel f32 lerp in 0..1, white prepended when the first event is not at beat 0. */
export class ColorAnim {
  readonly timeline: Timeline;
  readonly r: Float32Array;
  readonly g: Float32Array;
  readonly b: Float32Array;

  private constructor(timeline: Timeline, r: Float32Array, g: Float32Array, b: Float32Array) {
    this.timeline = timeline;
    this.r = r;
    this.g = g;
    this.b = b;
  }

  static fromEvents(events: ReadonlyArray<EventLike<RGB>>, env: EvalEnv): ColorAnim {
    const withDefault = needsDefault(events);
    const tl = eventTimeline(events, env, withDefault);
    const r = new Float32Array(tl.n);
    const g = new Float32Array(tl.n);
    const b = new Float32Array(tl.n);
    const off = withDefault ? 1 : 0;
    if (withDefault) {
      r[0] = 1;
      g[0] = 1;
      b[0] = 1;
    }
    events.forEach((e, k) => {
      const i = off + k * 2;
      r[i] = colorChannel(e.start[0]);
      g[i] = colorChannel(e.start[1]);
      b[i] = colorChannel(e.start[2]);
      r[i + 1] = colorChannel(e.end[0]);
      g[i + 1] = colorChannel(e.end[1]);
      b[i + 1] = colorChannel(e.end[2]);
    });
    return new ColorAnim(tl, r, g, b);
  }

  /** Writes the colour at t (0..1 per channel, f32) into `out` and returns it. */
  at(t: number, out: [number, number, number]): [number, number, number] {
    const tl = this.timeline;
    const i = tl.cursorAt(t);
    if (i + 1 >= tl.n) {
      out[0] = this.r[i] as number;
      out[1] = this.g[i] as number;
      out[2] = this.b[i] as number;
      return out;
    }
    const y = tl.yAt(i, t);
    out[0] = lerpAt(this.r, i, y);
    out[1] = lerpAt(this.g, i, y);
    out[2] = lerpAt(this.b, i, y);
    return out;
  }

  usesBezier(): boolean {
    return this.timeline.usesBezier();
  }
}

function lerpAt(v: Float32Array, i: number, y: number): number {
  const a = v[i] as number;
  const b = v[i + 1] as number;
  return fr(a + fr(fr(b - a) * y));
}

// ---------------------------------------------------------------------------------------------------------------
// GIF progress
// ---------------------------------------------------------------------------------------------------------------

/** Natural-speed loop keyframes are built up to this chart time (s). */
export const GIF_LOOP_LIMIT_SEC = 2000;

const P_NATURAL = 0;
const P_EVENT = 1;

/**
 * GIF progress track (F§9.7, D§6.2): outside events the GIF auto-plays at its natural speed (progress grows by
 * 1 per loop duration) from 0 at t = 0 before the first event, and from each event's end value after it; inside an
 * event the progress follows start -> end with the event's easing. Natural play is built up to 2000 s (or the
 * last event's end when later); after that the reached value is held. `gifEvents: []` = natural play from 0.
 * Values are f32 like Phira's keyframes; the progress keeps growing during natural play and the loop wraps in
 * `gifMsAt` exactly as Phira's frame lookup `(progress * total_ms) as u128 % total_ms` does.
 */
export class GifTrack {
  readonly totalMs: number;
  private readonly totalSec: number;
  private readonly kind: Uint8Array;
  private readonly t0: Float64Array;
  private readonly t1: Float64Array;
  private readonly v0: Float32Array;
  private readonly v1: Float32Array;
  private readonly tween: TweenTable;
  private readonly sorted: boolean;
  private hint = 0;

  constructor(events: ReadonlyArray<EventLike<number>>, env: EvalEnv, totalMs: number) {
    this.totalMs = totalMs;
    this.totalSec = totalMs / 1000;
    const kinds: number[] = [];
    const t0: number[] = [];
    const t1: number[] = [];
    const v0: number[] = [];
    const v1: number[] = [];
    const descs: (PhiraTweenDesc | null)[] = [];
    let curT = 0;
    let curV = 0;
    const natural = (until: number): void => {
      if (until > curT) {
        kinds.push(P_NATURAL);
        t0.push(curT);
        t1.push(until);
        v0.push(curV);
        v1.push(curV + (until - curT) / this.totalSec);
        descs.push(null);
      }
    };
    for (const e of events) {
      const ts = env.sec(e.startTime);
      const te = env.sec(e.endTime);
      natural(ts);
      kinds.push(P_EVENT);
      t0.push(ts);
      t1.push(te);
      v0.push(fr(e.start));
      v1.push(fr(e.end));
      descs.push(eventTweenDesc(e));
      curT = te;
      curV = fr(e.end);
    }
    natural(Math.max(GIF_LOOP_LIMIT_SEC, curT));
    const n = kinds.length;
    this.kind = Uint8Array.from(kinds);
    this.t0 = Float64Array.from(t0);
    this.t1 = Float64Array.from(t1);
    // Start values are 0 or an event's f32 end value, so storing them as f32 is exact.
    this.v0 = Float32Array.from(v0);
    this.v1 = Float32Array.from(v1);
    this.tween = new TweenTable(n);
    descs.forEach((d, i) => {
      if (d === null) this.tween.setStatic(i, LINEAR_TWEEN);
      else this.tween.setDesc(i, d, env.bezier);
    });
    this.sorted = isSortedTimes(this.t0);
  }

  /** Number of pieces (natural runs and events). */
  get pieces(): number {
    return this.kind.length;
  }

  private pieceValid(i: number, t: number): boolean {
    const n = this.kind.length;
    return (i === 0 || (this.t0[i] ?? Number.NaN) <= t) && (i + 1 >= n || (this.t0[i + 1] ?? Number.NaN) > t);
  }

  /** Active piece at t; a NaN t selects the last piece, like `Timeline.cursorAt` (Phira's `set_time` walks on). */
  private pieceAt(t: number): number {
    const n = this.kind.length;
    if (n <= 1) return 0;
    if (Number.isNaN(t)) return n - 1;
    if (!this.sorted) {
      let p = 0;
      for (let i = 0; i < n; i++) if ((this.t0[i] ?? Number.NaN) <= t) p = i;
      return p;
    }
    const h = this.hint;
    if (h < n && this.pieceValid(h, t)) return h;
    if (h + 1 < n && this.pieceValid(h + 1, t)) {
      this.hint = h + 1;
      return h + 1;
    }
    let i = Search.upperBound(this.t0, t) - 1;
    if (i < 0) i = 0;
    this.hint = i;
    return i;
  }

  /**
   * Progress at t (f32, not wrapped). NaN when the loop duration is not positive. A NaN t yields the last
   * keyframe's value (the end of the last piece), as every other Phira track does.
   */
  progressAt(t: number): number {
    if (!(this.totalMs > 0) || this.kind.length === 0) return Number.NaN;
    const i = this.pieceAt(t);
    if (Number.isNaN(t)) return this.v1[i] ?? Number.NaN;
    const t0 = this.t0[i] ?? Number.NaN;
    const t1 = this.t1[i] ?? Number.NaN;
    if (this.kind[i] === P_NATURAL) {
      return fr((this.v0[i] ?? 0) + (Math.min(t, t1) - t0) / this.totalSec);
    }
    const a = this.v0[i] ?? Number.NaN;
    if (t >= t1) return this.v1[i] ?? Number.NaN;
    const b = this.v1[i] ?? Number.NaN;
    const y = this.tween.y(i, fr((t - t0) / (t1 - t0)));
    return fr(a + fr(fr(b - a) * y));
  }

  /** Position in the loop in ms at t (Phira's frame lookup). */
  msAt(t: number): number {
    return gifMsAt(this.progressAt(t), this.totalMs);
  }

  usesBezier(): boolean {
    for (let i = 0; i < this.kind.length; i++) if (this.tween.isBezier(i)) return true;
    return false;
  }
}

/**
 * Phira's GIF frame position: `(progress * total_ms) as u128 % total_ms` (negative / NaN -> 0; the cast
 * truncates). Returns 0 for a non-positive loop duration (Phira would hang or panic there).
 */
export function gifMsAt(progress: number, totalMs: number): number {
  if (!(totalMs > 0)) return 0;
  const raw = progress * totalMs;
  if (Number.isNaN(raw) || raw <= 0) return 0;
  if (!Number.isFinite(raw)) return 0;
  return Math.floor(raw) % totalMs;
}

/**
 * Loop phase in [0, 1) equivalent to a progress value for Phira's frame lookup: natural auto-play makes the
 * progress grow past 1; the phase is what the "% of the loop" readouts show. `gifMsAt(phase, total)` equals
 * `gifMsAt(progress, total)` for progress >= 0 (both products are exact in f64).
 */
export function gifPhase(progress: number): number {
  if (Number.isNaN(progress) || progress <= 0) return 0;
  if (!Number.isFinite(progress)) return 0;
  return progress - Math.floor(progress);
}

// ---------------------------------------------------------------------------------------------------------------
// Sampling
// ---------------------------------------------------------------------------------------------------------------

/** Time of sample i of `n` evenly spaced samples over [t0, t1] (n = 1 -> t0). */
export function sampleTime(t0: number, t1: number, i: number, n: number): number {
  return n <= 1 ? t0 : t0 + ((t1 - t0) * i) / (n - 1);
}

/** Fills `out` with `fn` at out.length evenly spaced times over [t0, t1] (in increasing order: hints stay hot). */
export function sampleInto(fn: (t: number) => number, t0: number, t1: number, out: Float32Array | Float64Array): void {
  const n = out.length;
  for (let i = 0; i < n; i++) out[i] = fn(sampleTime(t0, t1, i, n));
}
